<template>
	<div v-if="permissionsLoaded" class="side-menu">
		<div class="search-container">
			<div class="inner-container">
				<StringInput id="menu-search-input" placeholder="Search menu..." :data="searchMenuText" allow-clear @update:preliminary="searchMenuText = $event" />
			</div>
		</div>
		<SideMenuSection title="Analyses" :expanded="false">
			<SideMenuItem v-if="hasPermission('past_snapshots') && matchesSearch('past snapshots')" to="/past-snapshots"> Past Snapshot </SideMenuItem>
			<SideMenuItem v-if="hasPermission('copy_v5_analyses') && matchesSearch('copy v5 analyses')" to="/copy-v5-analysis"> Copy V5 </SideMenuItem>
			<SideMenuItem v-if="hasPermission('copy_dmx_analyses') && matchesSearch('copy dmx analyses')" to="/copy-dmx-analysis"> Copy DMX </SideMenuItem>
		</SideMenuSection>
		<SideMenuSection v-if="hasPermission('login_image')" title="Customization" :expanded="false">
			<SideMenuItem v-if="matchesSearch('login settings')" to="/login_settings"> Login Settings </SideMenuItem>
		</SideMenuSection>
		<SideMenuSection title="Statistics" :expanded="false">
			<SideMenuItem v-if="hasPermission('usage_statistics') && matchesSearch('usage statistics')" to="/usage_statistics"> Usage Statistics </SideMenuItem>
			<SideMenuItem v-if="hasPermission('usage_statistics') && matchesSearch('module statistics')" to="/mongo_charts"> Module Statistics </SideMenuItem>
			<SideMenuItem v-if="hasPermission('argilla_statistics') && matchesSearch('argilla')" to="/statistics/argilla"> Argilla </SideMenuItem>
			<SideMenuItem v-if="hasPermission('one_dm') && matchesSearch('one datamaran')" to="/statistics/one-dm"> One Datamaran </SideMenuItem>
			<SideMenuItem v-if="hasPermission('usage_statistics') && matchesSearch('double materiality')" to="/statistics/double-materiality-edit">
				Double Materiality Edit Page
			</SideMenuItem>
			<SideMenuItem v-if="hasPermission('usage_statistics') && matchesSearch('issue maps categories')" to="/statistics/issue-categories"> Issue Map's Categories </SideMenuItem>
			<SideMenuItem v-if="hasPermission('usage_statistics') && matchesSearch('dmx monitoring')" to="/statistics/dmx-monitoring"> DMX Monitoring </SideMenuItem>
			<SideMenuItem v-if="hasPermission('usage_statistics') && matchesSearch('main stories')" to="/statistics/main-stories"> Main Stories </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection title="Users & Access">
			<SideMenuItem v-if="hasPermission('users') && matchesSearch('users')" to="/users"> Users </SideMenuItem>
			<SideMenuItem v-if="hasPermission('clients') && matchesSearch('clients')" to="/clients"> Clients </SideMenuItem>
			<SideMenuItem v-if="hasPermission('features') && matchesSearch('features')" to="/features"> Features </SideMenuItem>
			<SideMenuItem v-if="hasPermission('users') && matchesSearch('qa user data')" to="/test-user-data">QA user data </SideMenuItem>
			<Feature name="user_roles">
				<SideMenuItem v-if="hasPermission('roles') && matchesSearch('roles')" to="/roles"> Roles </SideMenuItem>
			</Feature>
		</SideMenuSection>

		<SideMenuSection v-if="hasPermission('news_dashboard')" title="News articles" :expanded="false">
			<SideMenuItem v-if="matchesSearch('news explorer')" to="/news_explorer"> News Explorer </SideMenuItem>
			<SideMenuItem v-if="matchesSearch('event detection')" to="/news_volume_list"> Event detection </SideMenuItem>
			<SideMenuItem v-if="matchesSearch('news url filters')" to="/news_url_filters"> News URL Filters </SideMenuItem>
			<SideMenuItem v-if="matchesSearch('news websources')" to="/news_websources"> News websources </SideMenuItem>
		</SideMenuSection>
		<SideMenuSection v-if="hasPermission('iro')" title="Impacts, Risks & Opportunities">
			<SideMenuItem v-if="matchesSearch('news search iro')" to="/iro/news/search"> News Search </SideMenuItem>
			<SideMenuItem v-if="matchesSearch('reports search iro')" to="/iro/reports/search"> Reports Search </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection title="Infrastructure">
			<SideMenuItem v-if="hasPermission('instances') && matchesSearch('instances')" to="/instances"> Instances </SideMenuItem>
			<SideMenuItem v-if="hasPermission('deployments') && matchesSearch('deployments')" to="/deployments"> Deployments </SideMenuItem>
			<Feature name="edit_changelog">
				<SideMenuItem v-if="hasPermission('release_updates') && matchesSearch('release updates changelogs')" to="/changelogs"> Release updates </SideMenuItem>
			</Feature>
			<Feature name="swap_production">
				<SideMenuItem v-if="hasPermission('production_deployment') && matchesSearch('prod release')" to="/release"> Prod release </SideMenuItem>
			</Feature>
			<SideMenuItem v-if="hasPermission('ip_whitelisting') && matchesSearch('ip whitelisting')" to="/ip-whitelisting"> Ip Whitelisting </SideMenuItem>
			<SideMenuItem v-if="matchesSearch('airflow data sync')" to="/datasync"> Airflow DataSync </SideMenuItem>
			<SideMenuItem v-if="hasPermission('instance_scheduler') && matchesSearch('instance scheduler')" to="/instance_schedules"> Instance Scheduler </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection v-if="hasPermission('initiatives')" title="Regulations" :expanded="false">
			<SideMenuItem v-if="hasPermission('initiatives') && matchesSearch('statistics')" to="/initiatives_statistics"> Statistics </SideMenuItem>
			<SideMenuItem v-if="hasPermission('initiatives') && matchesSearch('statistics')" to="/initiatives_search_data_consistency"> Search stats </SideMenuItem>
			<SideMenuItem v-if="hasPermission('issuers') && matchesSearch('policy makers')" to="/issuers"> Policy-makers </SideMenuItem>
			<SideMenuItem v-if="hasPermission('regional_unions') && matchesSearch('regional unions')" to="/regional_unions"> Regional Unions </SideMenuItem>
			<SideMenuItem v-if="hasPermission('initiatives') && matchesSearch('wip initiatives')" to="/initiatives_draft"> WIP Initiatives </SideMenuItem>
			<SideMenuItem v-if="hasPermission('initiatives') && matchesSearch('validated initiatives')" to="/initiatives"> Validated Initiatives </SideMenuItem>
			<SideMenuItem v-if="hasPermission('initiatives') && matchesSearch('linked curated searches')" to="/linked_curated_searches"> Linked Curated Searches </SideMenuItem>
			<SideMenuItem v-if="hasPermission('initiatives') && matchesSearch('regulation search synonyms')" to="/regulation_search_synonyms"> Regulation Search Synonyms </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection title="Ontology view" :expanded="false">
			<SideMenuItem v-if="matchesSearch('topics (live)')" to="/topicsView"> Topics (Live) </SideMenuItem>
			<SideMenuItem v-if="matchesSearch('factors (live)')" to="/conceptsView"> Factors (Live) </SideMenuItem>
			<SideMenuItem v-if="matchesSearch('terms (active)')" to="/termsView"> Terms (Active) </SideMenuItem>
			<SideMenuItem v-if="matchesSearch('gri/sasb mappings')" to="/gri_sasb_mappings"> GRI/SASB Mappings </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection v-if="hasPermission('topics') || hasPermission('factors') || hasPermission('terms')" title="Ontology update" :expanded="false">
			<SideMenuItem v-if="hasPermission('topics') && matchesSearch('ontology topics')" to="/topics"> Topics </SideMenuItem>
			<SideMenuItem v-if="hasPermission('factors') && matchesSearch('ontology concepts')" to="/concepts"> Factors </SideMenuItem>
			<SideMenuItem v-if="hasPermission('terms') && matchesSearch('ontology terms')" to="/terms"> Terms </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection title="Ontology" :expanded="false">
			<SideMenuItem to="/ontology_versions"> Versions </SideMenuItem>
			<SideMenuItem to="/ontology_nlp_check"> NLP check </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection title="Documents" :expanded="false">
			<SideMenuItem v-if="hasPermission('reports') && matchesSearch('reports')" to="/document/report"> Reports </SideMenuItem>
			<Feature name="comparison_report">
				<SideMenuItem v-if="matchesSearch('comparison_report')" to="/comparison_report"> Comparison Report </SideMenuItem>
			</Feature>
		</SideMenuSection>

		<SideMenuSection v-if="hasPermission('ontology_deployment')" title="Ontology deployment" :expanded="false">
			<SideMenuItem v-if="hasPermission('ontology_deployment')" to="/update_issue_mappings"> Update Issue Mappings </SideMenuItem>
			<SideMenuItem v-if="hasPermission('ontology_deployment')" to="/update_gri_sasb_mappings"> Update GRI/SASB Mappings </SideMenuItem>
			<SideMenuItem v-if="hasPermission('ontology_deployment')" to="/deploy_ontology"> Deploy Ontology </SideMenuItem>
			<SideMenuItem v-if="hasPermission('ontology_deployment')" to="/ontology_pipeline_operations"> Pipeline operations </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection title="Sectors" :expanded="false">
			<SideMenuItem v-if="matchesSearch('sics sectors industries')" to="/sicSectors"> SICS industries </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection title="Organizations" :expanded="false">
			<SideMenuItem v-if="matchesSearch('organizations')" to="/organizations"> Organizations </SideMenuItem>
		</SideMenuSection>

		<SideMenuSection v-if="hasPermission('pipeline_monitoring')" title="Pipeline monitoring" :expanded="false">
			<SideMenuItem v-if="hasPermission('pipeline_monitoring') && matchesSearch('pipeline monitoring')" to="/pipeline_monitoring_overview">
				Pipeline monitoring overview
			</SideMenuItem>
		</SideMenuSection>
	</div>
</template>

<script>
import { isProd } from '@/utility/common'
import { Feature, SideMenuItem, SideMenuSection, StringInput } from 'dmui_components'
import { ref, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import permissionChecker from './permissionChecker'

export function matchesSearch(searchText, key) {
	const regex = new RegExp(`.*${searchText}.*`, 'i')
	return searchText === '' || regex.test(key)
}

export default {
	name: 'SubmaranSideMenu',

	components: {
		SideMenuSection,
		SideMenuItem,
		Feature,
		StringInput
	},

	setup() {
		const { hasPermission, permissionsLoaded } = permissionChecker()
		const route = useRoute()
		const searchMenuText = ref('')
		/**
		 * This effect is to redirect the user to the first default
		 * link allowed as per permissions
		 */
		watchEffect(() => {
			if (permissionsLoaded.value && route.path === '/') {
				setTimeout(() => {
					document.querySelector('.side-menu-item')?.click()
				}, 100)
			}
		})

		return {
			hasPermission,
			permissionsLoaded,
			isProd,
			searchMenuText,

			matchesSearch: (key) => matchesSearch(searchMenuText.value, key)
		}
	}
}
</script>

<style lang="less" scoped>
:deep(.router-link-active) {
	background: var(--pv-gray-200);
}
.side-menu {
	margin: 20px;
	width: 250px;
	position: fixed;
	overflow: scroll;
	// accounts for space occupied by header + margins
	height: calc(100% - 75px);
	z-index: 1;
	background-color: white;
}

.search-container {
	position: sticky;
	top: 0;
	z-index: 1;
	background: white;
	margin-bottom: 5px;

	.inner-container {
		width: 95%;
	}
}
</style>
